/**
 * The arithmetic behind the analytics dashboard's window picker and its stat
 * tiles, kept out of the component so it can be reasoned about — and run — on
 * its own.
 *
 * No imports, deliberately, on the precedent `model-usage.ts` set: Node 24
 * strips types, so a module with no imports and no JSX runs directly under
 * `node analytics-window.ts`, and the self-check at the foot of this file is
 * what `pnpm --filter @empite/admin-portal test` executes. The two string
 * unions below are re-declared rather than imported from `@empite/ui-components`
 * for that reason; they are the same unions, so a value produced here is
 * assignable to a `StatDelta` without a cast.
 *
 * Everything here exists because it would fail SILENTLY if it drifted:
 *
 *   * a delta whose arrow disagreed with the figure printed beside it,
 *   * a "not measured" window rendered as a row of zeros,
 *   * a picker that puts `since`, `until` AND `days` in one URL, which the
 *     server refuses with a 400 rather than picking two of the three,
 *   * a "figures run to yesterday" notice that never fires, so a dashboard
 *     read at 09:00 looks like it includes this morning.
 */

/** The window the picker holds, and the shape of `/analytics`'s URL search
 *  parameters. Inclusive ISO dates (YYYY-MM-DD); `days` is an inclusive span. */
export interface AnalyticsSearch {
  since?: string;
  until?: string;
  days?: number;
}

/** The spans the picker offers. The server's own default is 30, and its
 *  ceiling is 90 — asking for more is clamped and reported as `clamped`,
 *  never refused, so nothing here has to mirror the bound. */
export const PRESET_DAYS: number[] = [7, 30, 90];

/** What the server does with no window at all. Mirrored only so the picker
 *  can show the right option BEFORE the first response arrives; every label
 *  on the screen is taken from the window the response states. */
export const DEFAULT_DAYS = 30;

/**
 * What the one picker control is set to.
 *
 * A single control rather than two, because the two modes are mutually
 * exclusive on the wire: `since` + `until` + `days` over-determines a window
 * and the Admin API answers 400 rather than dropping one of the three. A
 * screen with a live day-count box AND a live date range would produce that
 * combination by simply being filled in.
 */
export type PickerValue = { kind: "recent"; days: number } | { kind: "range" };

/** The picker's value for a URL. Either bound present means a date range was
 *  asked for: a `days` beside it is what the server refuses, so the range is
 *  what the control shows and the submit below is what stops it recurring. */
export function pickerValue(search: AnalyticsSearch): PickerValue {
  if (search.since !== undefined || search.until !== undefined) {
    return { kind: "range" };
  }
  return { kind: "recent", days: search.days ?? DEFAULT_DAYS };
}

/**
 * The day counts to offer, which is the presets plus whatever the URL is
 * actually carrying.
 *
 * A hand-written `?days=45` is a legitimate link, and a picker showing "Last
 * 30 days" over a 45-day answer would be the screen telling the operator the
 * wrong thing about the numbers under it. Sorted so the extra option lands
 * where a reader expects it rather than at the end.
 */
export function dayOptions(value: PickerValue): number[] {
  if (value.kind === "range" || PRESET_DAYS.includes(value.days)) {
    return [...PRESET_DAYS];
  }
  return [...PRESET_DAYS, value.days].sort((a, b) => a - b);
}

/** The search a preset produces. Both bounds are cleared: a `days` left
 *  beside a stale `since` is the over-determined combination the API refuses. */
export function recentSearch(days: number): AnalyticsSearch {
  return { days, since: undefined, until: undefined };
}

/** The search a date range produces, with `days` cleared for the same reason.
 *  An empty box is omitted rather than sent: the API reads one bound alone
 *  ("since, through the last complete day") and `?since=` as a 400. */
export function rangeSearch(since: string, until: string): AnalyticsSearch {
  return { since: since || undefined, until: until || undefined, days: undefined };
}

/** Which way a figure moved. Not whether that is good news — see
 *  `deltaSentiment`. The same union `@empite/ui-components` exports. */
export type DeltaDirection = "up" | "down" | "flat";

/** Whether the movement is good news. */
export type DeltaSentiment = "good" | "bad" | "neutral";

/** Which direction is the good one for a given measure. Most measures have no
 *  answer — "output tokens up" is neither good nor bad — and `neutral` is that
 *  absence rather than a third opinion. */
export type DeltaPolarity = "up-good" | "up-bad" | "neutral";

/** A percentage change as it will be shown. */
export interface FormattedDelta {
  /** `null` is NO COMPARABLE BASELINE — the previous window was zero and this
   *  one is not — and the tile renders it as those words. */
  text: string | null;
  direction: DeltaDirection;
}

/**
 * A percentage change, rounded to the precision it is printed at, with the
 * arrow taken from the ROUNDED figure.
 *
 * Deriving the arrow from the raw number instead is how a tile comes to read
 * "▼ 0.0% down": a change of -0.04% is a fall, and at one decimal place it is
 * also nothing. Whatever the reader can see is what the arrow has to agree
 * with, so the rounding happens first and everything else follows it.
 *
 * `null` in, `null` out. An absent key — a money delta withheld from an
 * operator without `wallets:read` — never reaches here at all: the tile
 * carrying it has no value either, and a delta under an em dash would be a
 * movement in a figure the reader is not being shown.
 */
export function formatDelta(percent: number | null): FormattedDelta {
  // Not finite covers NaN and both infinities. None is reachable from the
  // server — percentDelta answers null rather than dividing by zero — but a
  // figure that arrived as one would otherwise print as "Infinity%" and be
  // read as a real reading.
  if (percent === null || !Number.isFinite(percent)) {
    return { text: null, direction: "flat" };
  }
  const rounded = Math.round(percent * 10) / 10;
  // The guard is the whole mechanism, and it is asked about the ROUNDED
  // figure: a change of -0.04% is a fall that prints as "0.0%", and asking
  // `percent === 0` instead would send it down the branch below and label it
  // "▼ 0.0% down". `-0 === 0` is true, so a rounded-away fall lands here.
  // Past this point the sign of `rounded` and the sign of `percent` cannot
  // differ, which is why the direction below may be read off either.
  if (rounded === 0) {
    return { text: "0.0%", direction: "flat" };
  }
  return {
    text: `${rounded > 0 ? "+" : ""}${rounded.toFixed(1)}%`,
    direction: rounded > 0 ? "up" : "down",
  };
}

/** Whether a movement is good news, which only two of the dashboard's measures
 *  have an opinion about. A flat reading is never good or bad. */
export function deltaSentiment(direction: DeltaDirection, polarity: DeltaPolarity): DeltaSentiment {
  if (polarity === "neutral" || direction === "flat") {
    return "neutral";
  }
  const good = polarity === "up-good" ? direction === "up" : direction === "down";
  return good ? "good" : "bad";
}

/**
 * A fraction in [0, 1] as a percentage, or `null` when it is not a number.
 *
 * `failure_rate` is a FRACTION on the wire and a percentage on the screen, and
 * the conversion lives here rather than inline so the two places that show it
 * cannot pick different precisions for the same reading. Two decimal places
 * because the interesting failure rates are fractions of a percent.
 */
export function formatRate(fraction: number): string | null {
  if (!Number.isFinite(fraction)) {
    return null;
  }
  return `${(fraction * 100).toFixed(2)}%`;
}

/**
 * Whether the window contains any measurement at all.
 *
 * `available_from` is the first day the rollups reach, and `null` means they
 * reach nothing yet. A window entirely before it is the same fact wearing a
 * different shape, and BOTH have to be told apart from a quiet window: the
 * totals come back as zeros either way, and "nobody used the platform" is not
 * something to read off a sweep that had not run.
 *
 * ISO dates compare as strings because YYYY-MM-DD is ordered the same way
 * lexicographically as it is chronologically — the reason the format is
 * written that way round.
 */
export function windowMeasured(availableFrom: string | null, until: string): boolean {
  return availableFrom !== null && availableFrom <= until;
}

/**
 * The last day the caller asked to be told about.
 *
 * An explicit `until` is the ask. Everything else — a preset, or no window at
 * all — is "the last N days" counted back from now, and what an operator means
 * by that is a span ending TODAY. `toISOString` states the UTC date, which is
 * the day the rollups are bucketed by; using the local date would move the
 * question by one for half the planet.
 */
export function requestedUntil(search: AnalyticsSearch, now: Date): string {
  return search.until ?? now.toISOString().slice(0, 10);
}

/** Whether the answer stops short of the day that was asked about, which is
 *  what the "today is not a finished day" line exists to say. `through` is the
 *  last day the rollups can be complete for, and it is yesterday in UTC. */
export function endsBeforeAsk(through: string, asked: string): boolean {
  return through < asked;
}

/* ------------------------------------------------------------------------ *
 * Self-check. Runs under `node analytics-window.ts`; the guard keeps it out
 * of the browser bundle, where `import.meta.main` is simply undefined.
 *
 * The double cast rather than a `declare global`: this file is compiled by
 * the portal's own tsconfig and TypeScript 5.7 does not declare
 * import.meta.main, and an augmentation would be a lie told to every other
 * file in the program.
 * ------------------------------------------------------------------------ */

/**
 * Serialise for comparison, with an ABSENT key kept distinct from one holding
 * undefined.
 *
 * Plain `JSON.stringify` drops an undefined-valued key entirely, so
 * `{ days: 90 }` and `{ days: 90, since: undefined, until: undefined }` print
 * identically — and the two searches below whose whole job is to CLEAR the
 * other mode's parameter would then be asserted by a comparison that cannot
 * see whether they cleared anything. The replacer is called for every own key,
 * including the undefined ones, so naming them here brings them back.
 */
function show(value: unknown): string {
  return JSON.stringify(value, (_key, held: unknown) => (held === undefined ? "<undefined>" : held));
}

function check(name: string, actual: unknown, expected: unknown): void {
  const a = show(actual);
  const b = show(expected);
  if (a !== b) {
    throw new Error(`analytics-window: ${name}: expected ${b}, got ${a}`);
  }
}

function selfCheck(): void {
  // The picker reads a URL.
  check("no window is the default preset", pickerValue({}), { kind: "recent", days: 30 });
  check("a day count is a preset", pickerValue({ days: 7 }), { kind: "recent", days: 7 });
  check("a since alone is a range", pickerValue({ since: "2026-01-01" }), { kind: "range" });
  check("an until alone is a range", pickerValue({ until: "2026-01-31" }), { kind: "range" });
  // The combination the API refuses still reads as a range, so the picker
  // shows the bounds rather than a day count that is not being applied.
  check(
    "an over-determined URL reads as a range",
    pickerValue({ since: "2026-01-01", until: "2026-01-31", days: 7 }),
    { kind: "range" },
  );

  check("the presets are offered", dayOptions({ kind: "recent", days: 30 }), [7, 30, 90]);
  check("a range offers the presets", dayOptions({ kind: "range" }), [7, 30, 90]);
  check("an odd day count joins them, in order", dayOptions({ kind: "recent", days: 45 }), [
    7, 30, 45, 90,
  ]);
  check("a day count below them sorts first", dayOptions({ kind: "recent", days: 1 }), [
    1, 7, 30, 90,
  ]);

  // A submit never produces the over-determined combination, from either side.
  check("a preset clears both bounds", recentSearch(90), {
    days: 90,
    since: undefined,
    until: undefined,
  });
  check("a range clears the day count", rangeSearch("2026-01-01", "2026-01-31"), {
    since: "2026-01-01",
    until: "2026-01-31",
    days: undefined,
  });
  check("an empty box is omitted, not sent empty", rangeSearch("", "2026-01-31"), {
    since: undefined,
    until: "2026-01-31",
    days: undefined,
  });

  // Deltas. The arrow follows the printed figure, never the raw one.
  check("a rise", formatDelta(12.35), { text: "+12.4%", direction: "up" });
  check("a fall", formatDelta(-8.04), { text: "-8.0%", direction: "down" });
  check("exactly no change", formatDelta(0), { text: "0.0%", direction: "flat" });
  check("a rise too small to print is flat", formatDelta(0.04), {
    text: "0.0%",
    direction: "flat",
  });
  check("a fall too small to print is flat, not down", formatDelta(-0.04), {
    text: "0.0%",
    direction: "flat",
  });
  check("no baseline", formatDelta(null), { text: null, direction: "flat" });
  check("a non-finite figure is no baseline", formatDelta(Number.POSITIVE_INFINITY), {
    text: null,
    direction: "flat",
  });
  check("NaN is no baseline", formatDelta(Number.NaN), { text: null, direction: "flat" });
  // A big rise keeps its sign and its precision rather than being abbreviated.
  check("a large rise", formatDelta(1250), { text: "+1250.0%", direction: "up" });

  check("revenue up is good", deltaSentiment("up", "up-good"), "good");
  check("revenue down is bad", deltaSentiment("down", "up-good"), "bad");
  check("failures up is bad", deltaSentiment("up", "up-bad"), "bad");
  check("failures down is good", deltaSentiment("down", "up-bad"), "good");
  check("tokens up is neither", deltaSentiment("up", "neutral"), "neutral");
  check("flat is never good news", deltaSentiment("flat", "up-good"), "neutral");
  check("flat is never bad news", deltaSentiment("flat", "up-bad"), "neutral");

  // A rate is a fraction on the wire and a percentage on the screen.
  check("a rate", formatRate(0.0123), "1.23%");
  check("no failures", formatRate(0), "0.00%");
  check("everything failed", formatRate(1), "100.00%");
  // A fraction of a percent stays visible rather than rounding to nothing.
  check("a small rate keeps its digits", formatRate(0.0002), "0.02%");
  check("a non-finite rate is not available", formatRate(Number.NaN), null);

  // Measured, quiet and not-yet-measured are three different windows.
  check("nothing rolled up yet", windowMeasured(null, "2026-08-27"), false);
  check("the window predates the rollups", windowMeasured("2026-08-01", "2026-07-31"), false);
  check("the rollups start inside the window", windowMeasured("2026-08-01", "2026-08-27"), true);
  check("the rollups start on the last day", windowMeasured("2026-08-27", "2026-08-27"), true);

  // The ask, and whether the answer reaches it.
  const noon = new Date("2026-08-28T12:00:00Z");
  check("an explicit until is the ask", requestedUntil({ until: "2026-01-31" }, noon), "2026-01-31");
  check("a preset asks about today", requestedUntil({ days: 7 }, noon), "2026-08-28");
  check("no window asks about today", requestedUntil({}, noon), "2026-08-28");
  // 00:30 UTC is the previous day in every timezone west of Greenwich, so a
  // local-date reading of the same instant would answer 2026-08-27 here.
  check(
    "the ask is a UTC day",
    requestedUntil({}, new Date("2026-08-28T00:30:00Z")),
    "2026-08-28",
  );

  check("yesterday falls short of today", endsBeforeAsk("2026-08-27", "2026-08-28"), true);
  check("a historical window is reached", endsBeforeAsk("2026-08-27", "2026-01-31"), false);
  check("an ask that lands on through is reached", endsBeforeAsk("2026-08-27", "2026-08-27"), false);
  // Across a month boundary, where a numeric comparison of the day-of-month
  // alone would answer the opposite.
  check("across a month end", endsBeforeAsk("2026-07-31", "2026-08-01"), true);

  console.log("analytics-window: ok");
}

if ((import.meta as unknown as { main?: boolean }).main === true) {
  selfCheck();
}
